<template>
    <div class="step-indicator">
        <div class="track-indicator">
            <div
                class="track"
                :style="styleTrack"
            ></div>
        </div>

        <div class="steps">
            <div
                v-for="(step, index) in steps"
                :key="index"
                class="step"
                @click="goToStep(index)"
            >
                <div
                    class="number"
                    :class="getStepClass(index)"
                >
                    {{ index + 1 }}
                </div>

                <h1 class="title">
                    {{ step.title }}
                </h1>

                <div
                    v-if="step.description"
                    class="description"
                >
                    {{ step.description }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type StepIndicatorStep = {
    title: string
    description?: string
}

const props = defineProps<{
    currentStep: number
    steps: Readonly<StepIndicatorStep[]>
}>()

const emit = defineEmits<{
    'go-to-step': [step: number]
}>()

const totalSteps = computed(() => props.steps.length)

const getStepClass = (step: number) =>
    step <= props.currentStep ? 'active' : ''

const goToStep = (step: number) => {
    if (step >= totalSteps.value || step < 0) {
        return
    }

    emit('go-to-step', step)
}

const styleTrack = computed(
    () => `width: ${(100 * props.currentStep) / (totalSteps.value - 1)}%`
)
</script>
<style lang="scss" scoped>
.step-indicator {
    position: relative;
    display: flex;

    .track-indicator {
        position: absolute;
        top: 19px;
        height: 2px;
        background-color: var(--grey-200);
        left: 50px;
        right: 50px;

        .track {
            width: 0;
            height: 100%;
            background-color: var(--green);
            transition: width 0.12s var(--timing-function-easing-emphasized);
        }
    }

    .steps {
        width: 100%;
        display: flex;
        justify-content: space-between;

        .step {
            flex: 1 0 auto;
            max-width: 102px;
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;

            .number {
                width: 38px;
                height: 38px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: var(--stone-3);
                font-family: $parcel-font-stack;
                font-style: normal;
                font-weight: 400;
                font-size: 18px;
                color: var(--slate);
                transition: all 0.12s var(--timing-function-easing-emphasized);
                cursor: not-allowed;

                &.active {
                    background-color: var(--green);
                    color: var(--white);
                    transition: all 0.24s
                        var(--timing-function-easing-emphasized) 0.12s;
                    cursor: pointer;
                }
            }

            .title,
            .description {
                text-align: center;
                font-style: normal;
                color: var(--slate);
                line-height: 20px;
            }

            .title {
                position: absolute;
                top: 42px;
                font-family: $sectra-font-stack;
                font-weight: 400;
                font-size: 16px;
            }

            .description {
                font-family: $effra-font-stack;
                font-weight: 400;
                font-size: 12px;
            }
        }
    }
}
</style>
